import {cloneDeep} from 'lodash';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NzTableComponent} from 'ng-zorro-antd/table';
import {firstValueFrom, Subject} from 'rxjs';
import {IUser} from '../../models/auth';
import {PatientMeasurementCreatorDTO, PatientMeasurementResponseDTO} from '../../dtos/measurement';
import {IPatientMeasurement, MeasurementAction, MeasurementState} from '../../models/measurement';
import {StorageService} from '../../services/storage/storage.service';
import {Store} from '@ngxs/store';
import {Loader} from '../../models/loader/loader.redux';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {Logger} from '../../class/helper/logger.class';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';

@Component({
  selector: 'app-all-measurements-table',
  templateUrl: './all-measurements-table.component.html',
  styleUrls: ['./all-measurements-table.component.scss'],
  animations: [fadeInAnimation],
})
export class AllMeasurementsTableComponent implements OnInit, OnDestroy {
  confirmModal?: NzModalRef;
  @ViewChild('measurementsTable', { static: false }) nzTableMeasurements?: NzTableComponent<any>;
  @Input() historial: PatientMeasurementResponseDTO;
  @Input() patientId: string;
  @Input() expertId: string;
  @Input() clinicId: string;
  @Input() showActions = true;
  @Input() tableIndex: number;
  @Output() changeRows: EventEmitter<{ action: 'edit' | 'delete' }> = new EventEmitter<{ action: 'edit' | 'delete' }>();

  registerInfo: PatientMeasurementCreatorDTO;

  private destroy$ = new Subject();
  userLogged: IUser;
  constructor(
    private storageService: StorageService,
    private store: Store,
    private modal: NzModalService,
    private messageAlertService: MessageAlertService
  ) { }

  ngOnInit(): void { }

  /**
   * Obtiene información de un registro de la tabla, para mostrar
   * quién realizó el registro.
   * @param register registro de tipo IPatientMeasurement
   * @param indexRow índice del renglón
   * @param indexCol índice de la columna
   */
  async onVewInfoRegister(
    register: IPatientMeasurement,
    indexRow: number,
    indexCol: number
  ): Promise<void> {
    this.userLogged = this.storageService.getLocalItem<{ user: IUser; token: string; }>('user')?.user;

    try {
      if (
        register.patientId &&
        register.expertId &&
        !register.automaticallyGenerated
      ) {
        await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo información del registro ...'))));
        await firstValueFrom(this.store.dispatch(
          new MeasurementAction.GetPatientMeasurementCreator(
            this.clinicId,
            register.patientId,
            register.expertId,
            register._id
          )));
        this.registerInfo = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getRegisterInfo))));
        // tslint:disable:no-string-literal
        this.registerInfo['_id'] = register._id;
        this.registerInfo['indexRow'] = indexRow;
        this.registerInfo['indexCol'] = indexCol;
      }
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Determina si una columna de la tabla es o no visible
   * @param measurement medida
   */
  onToggleShowColumnTable(measurement): void {
    measurement.hide = !measurement.hide;
  }

  /**
   * Habilita una fila de registros para modo edición.
   * @param item item
   */
  onEditMode(item): void {
    item.patientMeasurements.forEach((measure, index) => {
      if (measure.value !== '-') {
        measure.newValue = measure.value;
        measure.edit = true;
      } else {
        measure.newValue = 0.1;
        measure.edit = true;
        measure.measureUnitId =
          this.historial.headers[index]?.defaultMeasurement._id;
        measure.measurementId = this.historial.headers[index]?._id;
      }
    });
    item.edit = true;
  }

  /**
   * Cambia el valor del registro dependiendo si es de tipo string o numérico.
   * @param item item
   * @param index índice
   * @param value valor
   */
  onChangeInputValue(item, index: number, value): void {
    const measurement = item.patientMeasurements[index];
    if (!measurement) {
      return;
    }
    if (typeof measurement.value === 'string') {
      item.patientMeasurements[index].value = value;
      return;
    }
    measurement.value += measurement.value === 0 ? 0.1 : 0;
  }

  /**
   * Guardar en base de datos los cambios en la fila de registros
   * @param item item
   */
  async onSaveRow(item): Promise<void> {
    const data: {
      patientMeasurementId: string;
      measureUnitId: string;
      value: number;
      measurementId: string;
    }[] = [];
    if (item) {
      item.patientMeasurements.forEach((measure) => {
        if (typeof measure.value !== 'string') {
          data.push({
            measureUnitId: measure.measureUnitId,
            patientMeasurementId: measure.metadata?.generated ? '' : measure?._id,
            measurementId: measure.measurementId,
            value: measure.newValue,
          });
        }
      });
    }
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando valores ...'))));
      await firstValueFrom(this.store.dispatch(
        new MeasurementAction.Put(
          this.clinicId,
          this.patientId,
          this.expertId,
          data
        )));
      this.changeRows.emit({ action: 'edit' });
    } catch (e) {
      this.messageAlertService.createMessage('error', 'Ha ocurrido un error al guardar los valores', 3000);
      Logger.log('Ocurrió un error', e?.message);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Elimina todos los registros de la fila.
   * @param item item
   */
  async onDelete(item): Promise<void> {
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Estás seguro de eliminar los registros de la fila?',
      nzContent: 'Esta acción no se podrá deshacer.',
      nzOkText: 'SÍ QUIERO ELIMINARLO',
      nzCancelText: 'NO QUIERO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm modal-confirm-medium-size',
      nzClosable: false,
      nzOnOk: async () => {
        try {
          await firstValueFrom((this.store.dispatch(new Loader(true, 'Eliminando registros ...'))));
          const measurement = item.patientMeasurements.find((measure) => measure._id && !measure.metadata?.generated);
          if (!measurement) return;
          await firstValueFrom(this.store.dispatch(
            new MeasurementAction.DeleteMeasurementRow(
              this.clinicId,
              this.patientId,
              this.expertId,
              measurement._id
            )));
          this.changeRows.emit({ action: 'delete' });
        } catch (e) {
          Logger.log('Ocurrio un error', e?.message);
        } finally {
          await firstValueFrom((this.store.dispatch(new Loader(false))));
        }
      },
      nzOnCancel: () => { },
    });
  }

  /**
   * Sale del modo de edición.
   * @param item item
   */
  onCancelEdit(item): void {
    item.edit = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.destroy$.complete();
  }
}
